import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Package, Upload, CheckSquare, Square, Loader2, Sparkles,
  Edit3, ChevronRight, AlertTriangle, CheckCircle, X,
  Zap, ChevronDown, ChevronUp, Save, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const METHOD_COLORS = {
  GET:    "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  POST:   "bg-blue-500/20 text-blue-400 border-blue-500/30",
  PUT:    "bg-amber-500/20 text-amber-400 border-amber-500/30",
  PATCH:  "bg-purple-500/20 text-purple-400 border-purple-500/30",
  DELETE: "bg-red-500/20 text-red-400 border-red-500/30",
};

// ── Step 1: Upload ────────────────────────────────────────────────────────────
function UploadZone({ onParsed }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.name.endsWith(".json")) {
      toast.error("Please upload a Postman collection JSON file (.json)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/imports/parse-collection`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      onParsed(res.data);
      toast.success(`Parsed "${res.data.collection_name}" — ${res.data.total} endpoints found`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse collection");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors cursor-pointer
        ${dragging ? "border-[#D946EF] bg-[#D946EF]/5" : "border-white/20 hover:border-white/40"}`}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); doUpload(e.dataTransfer.files[0]); }}
      onClick={() => fileRef.current.click()}
    >
      <input ref={fileRef} type="file" accept=".json" className="hidden"
        onChange={e => doUpload(e.target.files[0])} />

      {uploading ? (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-10 h-10 text-[#D946EF] animate-spin" />
          <p className="text-slate-400">Parsing collection…</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="w-16 h-16 bg-[#D946EF]/10 rounded-xl flex items-center justify-center">
            <Upload className="w-8 h-8 text-[#D946EF]" />
          </div>
          <div>
            <p className="text-white font-semibold mb-1">Drop your Postman collection here</p>
            <p className="text-slate-400 text-sm">or click to browse — JSON format, max 5 MB</p>
          </div>
          <Badge className="bg-[#D946EF]/10 text-[#D946EF] border-[#D946EF]/20">Postman v2 / v2.1</Badge>
        </div>
      )}
    </div>
  );
}

// ── Generated test case preview card ─────────────────────────────────────────
function GeneratedTestCard({ tc, index, onEditAndSave, saving }) {
  const [expanded, setExpanded] = useState(index === 0);
  const steps = tc.api_steps || [];
  const totalAssertions = steps.reduce((sum, s) => sum + (s.assertions?.length || 0), 0);
  const chainedVars = steps.reduce((acc, s) => {
    Object.keys(s.extract || {}).forEach(v => acc.add(v));
    return acc;
  }, new Set());

  return (
    <Card className="glass-card border-white/10 rounded-xl">
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="w-8 h-8 bg-[#D946EF]/20 rounded-sm flex items-center justify-center flex-shrink-0">
          <Zap className="w-4 h-4 text-[#D946EF]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white text-sm truncate">{tc.name}</p>
          <p className="text-slate-500 text-xs">{steps.length} steps · {totalAssertions} assertions</p>
        </div>
        <div className="flex items-center gap-2">
          {chainedVars.size > 0 && (
            <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-xs">
              {chainedVars.size} chained var{chainedVars.size !== 1 ? "s" : ""}
            </Badge>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </div>

      {expanded && (
        <CardContent className="pt-0 pb-4 space-y-3">
          {tc.description && (
            <p className="text-slate-400 text-xs italic px-1">{tc.description}</p>
          )}

          {/* Steps list */}
          <div className="space-y-2">
            {steps.map((step, si) => (
              <div key={si} className="bg-black/30 rounded-sm px-3 py-2 border border-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-slate-600 text-xs">#{step.step_number}</span>
                  <Badge className={`text-xs font-mono border ${METHOD_COLORS[step.method] || METHOD_COLORS.GET}`}>
                    {step.method}
                  </Badge>
                  <span className="text-slate-300 text-xs font-mono truncate flex-1">{step.url}</span>
                </div>
                <p className="text-slate-400 text-xs ml-1 mb-1">{step.name}</p>

                {/* Assertions */}
                {step.assertions?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {step.assertions.map((a, ai) => (
                      <span key={ai} className="text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded px-1.5 py-0.5">
                        {a.type === "status_code" ? `status = ${a.expected}` :
                         a.type === "json_path" ? `${a.target} = ${a.expected}` :
                         a.type === "response_time" ? `time ≤ ${a.expected}ms` :
                         a.type === "body_contains" ? `contains "${a.expected}"` :
                         `${a.type}: ${a.expected}`}
                      </span>
                    ))}
                  </div>
                )}

                {/* Extract */}
                {Object.keys(step.extract || {}).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {Object.entries(step.extract).map(([varName, path]) => (
                      <span key={varName} className="text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded px-1.5 py-0.5">
                        → {`{{${varName}}`} from {path}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              className="bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm flex-1"
              onClick={() => onEditAndSave(tc, index)}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Edit3 className="w-3 h-3 mr-1" />}
              Edit &amp; Save
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ImportCollection() {
  const navigate = useNavigate();

  // Step 1: upload state
  const [collection, setCollection] = useState(null); // parsed collection from backend

  // Step 2: selection state
  const [selected, setSelected] = useState(new Set());
  const [groupStyle, setGroupStyle] = useState("separate"); // "one" | "separate"

  // Step 3: AI generation
  const [generating, setGenerating] = useState(false);

  // Step 4: Preview modal
  const [preview, setPreview] = useState(null); // { testCases: [...] }
  const [saving, setSaving] = useState(false);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const toggleAll = () => {
    if (!collection) return;
    if (selected.size === collection.endpoints.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(collection.endpoints.map((_, i) => i)));
    }
  };

  const toggleOne = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const selectedEndpoints = collection
    ? collection.endpoints.filter((_, i) => selected.has(i))
    : [];

  // Build manually — navigate to CreateAPITest with pre-filled steps
  const handleBuildManually = () => {
    if (!selectedEndpoints.length) { toast.error("Select at least one endpoint"); return; }
    const prefillSteps = selectedEndpoints.map((ep, i) => ({
      step_number: i + 1,
      name: ep.name,
      method: ep.method,
      url: ep.url,
      headers: ep.headers || {},
      body: ep.body || null,
    }));
    navigate("/create-api-test", {
      state: { prefillSteps, testName: collection.collection_name },
    });
  };

  // AI generate
  const handleGenerate = async () => {
    if (!selectedEndpoints.length) { toast.error("Select at least one endpoint"); return; }
    if (selectedEndpoints.length > 60) { toast.error("Max 60 endpoints. Deselect some."); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/imports/generate-tests`, {
        endpoints: selectedEndpoints,
        collection_name: collection.collection_name,
        style: groupStyle,
      }, { withCredentials: true });
      setPreview({ testCases: res.data.test_cases });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  // Save one test case (edit & save → navigate to CreateAPITest pre-filled)
  const handleEditAndSave = (tc, _idx) => {
    // Convert generated test case into CreateAPITest state format
    const prefillSteps = (tc.api_steps || []).map(step => ({
      method: step.method,
      url: step.url,
      headers: step.headers || {},
      body: step.body || null,
      name: step.name,
      assertions: step.assertions || [],
      extract: step.extract || {},
    }));
    navigate("/create-api-test", {
      state: {
        prefillSteps,
        testName: tc.name,
        testDescription: tc.description,
        baseUrl: tc.api_config?.base_url || "",
        apiConfig: tc.api_config,
      },
    });
  };

  // Save all test cases directly
  const handleSaveAll = async () => {
    if (!preview?.testCases?.length) return;
    setSaving(true);
    let savedCount = 0;
    let failedCount = 0;
    for (const tc of preview.testCases) {
      try {
        await axios.post(`${API}/test-cases`, {
          name: tc.name,
          description: tc.description || "",
          test_type: "api",
          api_config: tc.api_config || { base_url: "", variables: {}, default_headers: {} },
          api_steps: (tc.api_steps || []).map((s, i) => ({
            step_number: i + 1,
            name: s.name || `Step ${i + 1}`,
            method: s.method || "GET",
            url: s.url || "",
            headers: s.headers || {},
            body: s.body || null,
            assertions: s.assertions || [],
            extract: s.extract || {},
          })),
          steps: [],
          tags: tc.tags || ["ai-generated"],
          is_shared_with_team: false,
        }, { withCredentials: true });
        savedCount++;
      } catch {
        failedCount++;
      }
    }
    setSaving(false);
    if (savedCount > 0) toast.success(`Saved ${savedCount} test case${savedCount !== 1 ? "s" : ""}!`);
    if (failedCount > 0) toast.error(`${failedCount} test case${failedCount !== 1 ? "s" : ""} failed to save`);
    if (savedCount > 0) navigate("/dashboard", { state: { tab: "api-tests" } });
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#f8f9fb] text-slate-900">
      <div className="fixed inset-0 hero-glow" />
      <div className="relative z-10">
        <AppNav />
        <div className="max-w-4xl mx-auto px-6 py-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>

            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white"
                onClick={() => navigate("/dashboard")}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D946EF]/20 rounded-sm flex items-center justify-center">
                  <Package className="w-5 h-5 text-[#D946EF]" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Import Collection</h1>
                  <p className="text-slate-400 text-sm">Upload a Postman collection and build or AI-generate test cases</p>
                </div>
              </div>
            </div>

            {/* Step 1: Upload */}
            {!collection && (
              <Card className="glass-card border-white/10 rounded-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: "Unbounded, sans-serif" }}>
                    <span className="w-6 h-6 bg-[#D946EF]/20 text-[#D946EF] rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    Upload Collection
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <UploadZone onParsed={(data) => {
                    setCollection(data);
                    setSelected(new Set(data.endpoints.map((_, i) => i)));
                  }} />
                </CardContent>
              </Card>
            )}

            {/* Step 2 & 3: Select endpoints + actions */}
            {collection && (
              <div className="space-y-6">
                {/* Collection info */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-[#D946EF]" />
                    <div>
                      <h2 className="font-bold text-lg">{collection.collection_name}</h2>
                      <p className="text-slate-400 text-sm">{collection.total} endpoints found</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white"
                    onClick={() => { setCollection(null); setSelected(new Set()); }}>
                    <X className="w-4 h-4 mr-1" /> Change file
                  </Button>
                </div>

                {/* Endpoint selection */}
                <Card className="glass-card border-white/10 rounded-xl">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle style={{ fontFamily: "Unbounded, sans-serif" }} className="text-base">
                        Select Endpoints
                        <Badge className="ml-2 bg-white/10 text-slate-300 border-white/10">
                          {selected.size} / {collection.endpoints.length}
                        </Badge>
                      </CardTitle>
                      <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white text-xs"
                        onClick={toggleAll}>
                        {selected.size === collection.endpoints.length ? "Deselect All" : "Select All"}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ScrollArea className="max-h-80">
                      <div className="space-y-1 pr-2">
                        {collection.endpoints.map((ep, idx) => (
                          <div
                            key={idx}
                            className={`flex items-center gap-3 px-3 py-2 rounded-sm cursor-pointer transition-colors
                              ${selected.has(idx) ? "bg-white/5" : "hover:bg-white/3"}`}
                            onClick={() => toggleOne(idx)}
                          >
                            {selected.has(idx)
                              ? <CheckSquare className="w-4 h-4 text-[#00E599] flex-shrink-0" />
                              : <Square className="w-4 h-4 text-slate-600 flex-shrink-0" />
                            }
                            <Badge className={`text-xs font-mono border flex-shrink-0 ${METHOD_COLORS[ep.method] || METHOD_COLORS.GET}`}>
                              {ep.method}
                            </Badge>
                            <span className="text-slate-300 text-sm font-medium flex-shrink-0">{ep.name}</span>
                            {ep.folder && (
                              <span className="text-slate-600 text-xs truncate">{ep.folder}</span>
                            )}
                            <span className="text-slate-600 text-xs font-mono truncate ml-auto hidden md:block">
                              {ep.url.length > 60 ? ep.url.slice(0, 60) + "…" : ep.url}
                            </span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Grouping option (for AI generate) */}
                <Card className="glass-card border-white/10 rounded-xl">
                  <CardHeader className="pb-3">
                    <CardTitle style={{ fontFamily: "Unbounded, sans-serif" }} className="text-base">
                      Grouping
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        {
                          value: "separate",
                          label: "Separate test cases",
                          desc: "AI groups related endpoints into logical test cases (recommended)",
                        },
                        {
                          value: "one",
                          label: "One test case",
                          desc: "All selected endpoints in a single test case, in sequence",
                        },
                      ].map(opt => (
                        <div
                          key={opt.value}
                          className={`rounded-sm border p-3 cursor-pointer transition-colors
                            ${groupStyle === opt.value
                              ? "border-[#D946EF] bg-[#D946EF]/10"
                              : "border-white/10 hover:border-white/20"}`}
                          onClick={() => setGroupStyle(opt.value)}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <div className={`w-3 h-3 rounded-full border-2 flex-shrink-0
                              ${groupStyle === opt.value ? "border-[#D946EF] bg-[#D946EF]" : "border-slate-600"}`} />
                            <span className="text-sm font-semibold text-white">{opt.label}</span>
                          </div>
                          <p className="text-xs text-slate-400 ml-5">{opt.desc}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Action buttons */}
                {selected.size > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Button
                      className="bg-white/10 text-white hover:bg-white/20 rounded-sm py-6 font-semibold flex-col h-auto gap-1"
                      onClick={handleBuildManually}
                    >
                      <div className="flex items-center gap-2">
                        <Edit3 className="w-5 h-5 text-[#00E599]" />
                        Build Manually
                      </div>
                      <span className="text-xs text-slate-400 font-normal">
                        Pre-fill steps, edit assertions yourself
                      </span>
                    </Button>

                    <Button
                      className="bg-[#D946EF] text-white hover:bg-[#C026D3] rounded-sm py-6 font-semibold flex-col h-auto gap-1"
                      onClick={handleGenerate}
                      disabled={generating}
                    >
                      {generating ? (
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Generating…
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5" />
                          Auto-Generate with AI
                        </div>
                      )}
                      <span className="text-xs text-pink-200 font-normal">
                        {generating ? "Calling Gemini — this takes ~15s" : "Assertions + variable chaining via Gemini"}
                      </span>
                    </Button>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* ── Preview modal ───────────────────────────────────────────────── */}
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="bg-[#0B0F1A] border-white/10 text-white max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Unbounded, sans-serif" }} className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#D946EF]" />
              AI-Generated Test Cases
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Review the generated test cases. Click "Edit &amp; Save" to customise one, or "Save All" to save them directly.
            </DialogDescription>
          </DialogHeader>

          {preview && (
            <>
              <ScrollArea className="flex-1 overflow-y-auto pr-1 mt-2">
                <div className="space-y-3">
                  {preview.testCases.map((tc, i) => (
                    <GeneratedTestCard
                      key={i}
                      tc={tc}
                      index={i}
                      onEditAndSave={handleEditAndSave}
                      saving={saving}
                    />
                  ))}
                </div>
              </ScrollArea>

              <div className="flex gap-3 pt-4 border-t border-white/10 mt-2">
                <Button
                  variant="ghost"
                  className="text-slate-400 hover:text-white"
                  onClick={() => setPreview(null)}
                >
                  <X className="w-4 h-4 mr-1" /> Discard
                </Button>
                <Button
                  className="flex-1 bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold"
                  onClick={handleSaveAll}
                  disabled={saving}
                >
                  {saving
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <Save className="w-4 h-4 mr-2" />
                  }
                  Save All ({preview.testCases.length}) Test Cases
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
